import {
  Plus,
  Mic,
  Paperclip,
  FileText,
  Sparkles,
  ArrowUpRight,
  AlertCircle,
  BarChart,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { motion, AnimatePresence } from 'framer-motion'
import { useSourceOfTruth } from '@/hooks/useSourceOfTruth'
import { useDocumentUpload } from '@/hooks/useDocumentUpload'

interface ContextBuilderViewProps {
  onAction: (action: string, label: string) => void
  onRecordAudio?: () => void
}

const researchAgents = [
  { id: 'fundraising', title: 'Fundraising Agent', desc: 'Drafts investor memos, identifies VC matches, and practices Q&A.', icon: Sparkles, color: 'text-purple-400' },
  { id: 'sales', title: 'Sales Development', desc: 'Generates outbound scripts, identifies prospects, and handles objections.', icon: ArrowUpRight, color: 'text-green-400' },
  { id: 'marketing', title: 'Marketing Director', desc: 'Creates content calendars, ad copy, and brand positioning guides.', icon: BarChart, color: 'text-blue-400' },
]

export function ContextBuilderView({ onAction, onRecordAudio }: ContextBuilderViewProps) {
  const { domains, healthPercent, loading: sotLoading } = useSourceOfTruth()
  const upload = useDocumentUpload()
  const isHighContext = healthPercent >= 80

  // Build gap list from real domain data (missing or partial domains)
  const gaps = domains
    .filter((d) => d.status !== 'complete')
    .map((d) => ({
      domain: d.label,
      key: d.key,
      filled: d.filledFields,
      total: d.totalFields,
      status: d.status as 'missing' | 'partial',
    }))

  return (
    <div className="flex-1 p-4 md:p-12 overflow-auto w-full">
      {/* Hidden file input */}
      <input
        ref={upload.fileInputRef}
        type="file"
        accept=".pdf,.pptx,.txt,.md,.csv,.mp3"
        className="hidden"
        onChange={(e) => upload.handleFileChange(e)}
      />

      <div className="max-w-4xl mx-auto space-y-6 md:space-y-10">
        {/* Header */}
        <div className="flex flex-col gap-4 md:gap-6">
          <h2 className="text-2xl md:text-3xl font-bold">Add Context</h2>

          {/* Upload + Record Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <UploadCard onUpload={upload.triggerFilePicker} uploading={upload.status === 'uploading'} fileName={upload.fileName} />
            <RecordCard onAction={onAction} onRecordAudio={onRecordAudio} />
          </div>

          {/* Upload error */}
          {upload.status === 'error' && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{upload.error}</span>
              <Button variant="ghost" size="sm" className="ml-auto text-xs" onClick={upload.reset}>Dismiss</Button>
            </div>
          )}

          {/* Score Ring */}
          <ScoreRing score={healthPercent} isHigh={isHighContext} loading={sotLoading} />
        </div>

        {/* Dynamic Content */}
        <AnimatePresence mode="wait">
          {!isHighContext ? (
            <GapCards key="gaps" gaps={gaps} onUpload={upload.triggerFilePicker} />
          ) : (
            <AgentCards key="agents" onAction={onAction} />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

/* ── Private sub-components ─────────────────────────────────── */

function UploadCard({ onUpload, uploading, fileName }: { onUpload: () => void; uploading: boolean; fileName: string | null }) {
  return (
    <button
      className="md:col-span-2 p-5 md:p-6 rounded-3xl bg-[#f3f5f8] dark:bg-secondary border border-[#f3f5f8] dark:border-white/15 hover:bg-[#eef1f8] dark:hover:bg-tertiary/70 hover:border-[#c7d4f8] dark:hover:border-primary/35 transition-all text-left disabled:opacity-60 group"
      onClick={onUpload}
      disabled={uploading}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-white dark:bg-secondary/70 border border-[#dde3f2] dark:border-white/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-colors shrink-0">
          {uploading ? <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" /> : <Plus className="h-4 w-4 md:h-5 md:w-5" />}
        </div>
        <span className="font-semibold text-base md:text-lg leading-none">
          {uploading ? `Uploading ${fileName ?? 'file'}…` : 'Upload Anything'}
        </span>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
        {uploading
          ? 'Processing will begin automatically after upload.'
          : 'Supported file types: .pdf, .pptx, .txt, .md, .csv, .mp3. We will automatically organize them into the right categories.'}
      </p>
    </button>
  )
}

function RecordCard({
  onAction,
  onRecordAudio,
}: {
  onAction: (a: string, l: string) => void
  onRecordAudio?: () => void
}) {
  return (
    <button
      className="p-5 md:p-6 rounded-3xl bg-[#f3f5f8] dark:bg-secondary border border-[#f3f5f8] dark:border-white/15 hover:bg-[#eef1f8] dark:hover:bg-tertiary/70 hover:border-[#c7d4f8] dark:hover:border-primary/35 transition-all text-left"
      onClick={() => {
        if (onRecordAudio) {
          onRecordAudio()
          return
        }
        onAction('record', 'new-session')
      }}
    >
      <div className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-red-500 flex items-center justify-center text-white mb-3">
        <Mic className="h-4 w-4 md:h-5 md:w-5" />
      </div>
      <div>
        <div className="font-semibold text-base md:text-lg mb-2 leading-none">Record Audio</div>
        <p className="text-sm text-muted-foreground leading-relaxed">Capture mentor sessions, customer calls, or brain dumps.</p>
      </div>
    </button>
  )
}

function ScoreRing({ score, isHigh, loading }: { score: number; isHigh: boolean; loading: boolean }) {
  return (
    <div className="flex flex-col md:flex-row items-start md:items-center gap-4 bg-white dark:bg-[#23263b] p-4 md:p-6 rounded-2xl border border-slate-200 dark:border-[#454a6d]">
      <div className="relative h-16 w-16 md:h-20 md:w-20 flex-shrink-0 mx-auto md:mx-0">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 36 36">
          <path className="text-slate-200 dark:text-secondary" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
          <path
            className={cn('transition-all duration-1000 ease-out', isHigh ? 'text-accent-success' : 'text-accent-warning')}
            strokeDasharray={`${score}, 100`}
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none" stroke="currentColor" strokeWidth="3"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-bold text-lg md:text-xl">
          {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : `${score}%`}
        </div>
      </div>
      <div className="text-center md:text-left">
        <h3 className="text-base md:text-lg font-semibold mb-1">
          {isHigh ? 'Context Deep & Ready' : 'Context Needs Enrichment'}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground max-w-lg">
          {isHigh
            ? 'Your knowledge graph is robust enough to run advanced autonomous research agents.'
            : 'Critical gaps detected. Upload documents to improve accuracy before running deep research.'}
        </p>
      </div>
    </div>
  )
}

interface GapInfo {
  domain: string
  key: string
  filled: number
  total: number
  status: 'missing' | 'partial'
}

function GapCards({ gaps, onUpload }: { gaps: GapInfo[]; onUpload: () => void }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
      {gaps.length > 0 && (
        <div className="flex items-center gap-2 text-accent-warning font-medium">
          <AlertCircle className="h-5 w-5" />
          <span>{gaps.length} Gap{gaps.length !== 1 ? 's' : ''} Detected</span>
        </div>
      )}

      <div className="grid gap-4">
        {gaps.map((gap) => (
          <div
            key={gap.key}
            className="p-5 rounded-xl border border-slate-200 dark:border-[#454a6d] bg-white dark:bg-[#23263b] hover:bg-slate-50 dark:hover:bg-[#282941] transition-colors flex flex-col md:flex-row md:items-center gap-4"
          >
            <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-secondary flex items-center justify-center shrink-0">
              <FileText className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">{gap.domain}</h4>
                <span className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded uppercase font-medium',
                  gap.status === 'missing' ? 'bg-accent-warning/10 text-accent-warning' : 'bg-blue-500/10 text-blue-400'
                )}>
                  {gap.status === 'missing' ? 'Missing' : `${gap.filled}/${gap.total}`}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Upload documents related to <span className="font-medium text-foreground">{gap.domain}</span> to improve context.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={onUpload}>
              <Paperclip className="h-4 w-4 mr-2" />
              Upload
            </Button>
          </div>
        ))}
      </div>

      {gaps.length === 0 && (
        <div className="text-center text-muted-foreground py-8">
          <p className="text-sm">No critical gaps — keep adding context to unlock agents.</p>
        </div>
      )}
    </motion.div>
  )
}

function AgentCards({ onAction }: { onAction: (a: string, l: string) => void }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
      <div className="flex items-center gap-2 text-accent-success font-medium">
        <Sparkles className="h-5 w-5" />
        <span>Deep Research Agents Unlocked</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {researchAgents.map((agent) => (
          <button
            key={agent.id}
            onClick={() => onAction('research', agent.title)}
            className="flex flex-col text-left h-full p-6 rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-all group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
              <agent.icon className="h-24 w-24" />
            </div>
            <div className={cn('h-12 w-12 rounded-lg bg-background flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform', agent.color)}>
              <agent.icon className="h-6 w-6" />
            </div>
            <h3 className="font-bold text-lg mb-2">{agent.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">{agent.desc}</p>
            <div className="mt-auto flex items-center text-primary text-sm font-medium">
              Run Agent <ArrowUpRight className="h-4 w-4 ml-1" />
            </div>
          </button>
        ))}
      </div>

      <div className="p-4 rounded-xl bg-white dark:bg-[#23263b] border border-slate-200 dark:border-[#454a6d] mt-8">
        <h4 className="font-medium text-sm mb-2 text-muted-foreground">Standard Actions</h4>
        <div className="flex flex-wrap gap-2">
          {['Draft Blog Post', 'Competitor Analysis', 'Email Sequence', 'Feature Spec'].map((action) => (
            <button key={action} onClick={() => onAction('chat', action)} className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-secondary hover:bg-slate-200 dark:hover:bg-secondary/80 text-sm transition-colors">
              {action}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  )
}
